import { useId, useState } from 'react'
import { Image, Pressable, ScrollView, Text, View } from 'react-native'
import type { ImageSourcePropType } from 'react-native'
import { createPortal } from 'react-dom'
import { ChevronDown } from 'lucide-react-native'
import type { ComponentType } from 'react'
import type { LucideProps } from 'lucide-react-native'
import { useTranslation } from 'react-i18next'
import { colors } from '@chillout/core'

export interface SelectFieldOption {
  label: string
  value: string
  imageSource?: ImageSourcePropType
}

interface SelectFieldProps {
  label?: string
  value: string
  options: SelectFieldOption[]
  placeholder?: string
  error?: string
  helperText?: string
  emptyText?: string
  accessibilityLabel?: string
  leftIcon?: ComponentType<LucideProps>
  onValueChange: (value: string) => void
}

interface PanelPosition {
  left: number
  width: number
  maxHeight: number
  top?: number
  bottom?: number
}

const PANEL_MAX_HEIGHT = 320
const PANEL_MARGIN = 8
const PANEL_MIN_HEIGHT = 120

export function SelectField({
  label,
  value,
  options,
  placeholder,
  error,
  helperText,
  emptyText,
  accessibilityLabel,
  leftIcon: LeftIcon,
  onValueChange,
}: SelectFieldProps) {
  const { t } = useTranslation()
  const triggerId = useId()
  const [isOpen, setIsOpen] = useState(false)
  const [panelPos, setPanelPos] = useState<PanelPosition | null>(null)
  const selectedOption = options.find((o) => o.value === value)
  const isEmpty = options.length === 0

  function openAtPosition(left: number, top: number, bottom: number, width: number) {
    const spaceBelow = window.innerHeight - bottom - PANEL_MARGIN
    const spaceAbove = top - PANEL_MARGIN
    const openAbove = spaceBelow < PANEL_MAX_HEIGHT && spaceAbove > spaceBelow
    const maxHeight = Math.max(Math.min(PANEL_MAX_HEIGHT, openAbove ? spaceAbove : spaceBelow), PANEL_MIN_HEIGHT)
    setPanelPos({
      left,
      width,
      maxHeight,
      ...(openAbove ? { bottom: window.innerHeight - top + 4 } : { top: bottom + 4 }),
    })
    setIsOpen(true)
  }

  function handleOpen() {
    if (isEmpty) return
    // document.getElementById finds the element anywhere in the DOM tree,
    // including inside React Native Web Modal portals where ref-based
    // measurement gives wrong coordinates (ref points to RN instance, not DOM node)
    const domNode = document.getElementById(triggerId)
    if (domNode) {
      const rect = domNode.getBoundingClientRect()
      openAtPosition(rect.left, rect.top, rect.bottom, rect.width)
    }
  }

  function handleSelect(optionValue: string) {
    onValueChange(optionValue)
    setIsOpen(false)
  }

  const portal =
    isOpen && panelPos
      ? createPortal(
          <>
            <Pressable
              className="fixed inset-0"
              style={{ zIndex: 9998 }}
              onPress={() => setIsOpen(false)}
              accessibilityLabel="Close"
            />
            <View
              className="overflow-hidden rounded-md border border-border bg-canvas-card"
              style={{
                position: 'fixed' as 'absolute',
                top: panelPos.top,
                bottom: panelPos.bottom,
                left: panelPos.left,
                minWidth: panelPos.width,
                maxWidth: 320,
                zIndex: 9999,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.08,
                shadowRadius: 12,
              }}
            >
              <ScrollView style={{ maxHeight: panelPos.maxHeight }} bounces={false}>
                {options.map((option) => (
                  <Pressable
                    key={option.value}
                    onPress={() => handleSelect(option.value)}
                    accessibilityRole="menuitem"
                    accessibilityState={{ selected: option.value === value }}
                    className={`min-h-[56px] flex-row items-center gap-3 px-3 xl:min-h-[48px] ${option.value === value ? 'bg-accent/10' : ''}`}
                  >
                    {option.imageSource ? (
                      <View className="h-9 w-9 overflow-hidden rounded">
                        <Image
                          source={option.imageSource}
                          resizeMode="cover"
                          style={{ width: '100%', height: '100%' }}
                        />
                      </View>
                    ) : null}
                    <Text
                      className={`flex-1 text-base font-normal ${option.value === value ? 'text-accent' : 'text-primary'}`}
                      numberOfLines={1}
                    >
                      {option.label}
                    </Text>
                  </Pressable>
                ))}
              </ScrollView>
            </View>
          </>,
          document.body,
        )
      : null

  return (
    <View className="w-full">
      {label ? <Text className="mb-2 text-sm font-semibold text-primary">{label}</Text> : null}
      <Pressable
        nativeID={triggerId}
        onPress={handleOpen}
        disabled={isEmpty}
        accessibilityLabel={accessibilityLabel ?? label}
        accessibilityRole="combobox"
        accessibilityState={{ expanded: isOpen, disabled: isEmpty }}
        className={`h-14 xl:h-12 flex-row items-center justify-between gap-2 rounded-md border bg-canvas-card px-3 ${
          isEmpty ? 'opacity-50' : ''
        } ${error ? 'border-accent' : 'border-border'}`}
      >
        {LeftIcon ? <LeftIcon size={18} color={colors.text.secondary} strokeWidth={1.8} /> : null}
        <View className="flex-row items-center gap-2 flex-1 min-w-0">
          {selectedOption?.imageSource ? (
            <View className="h-8 w-8 shrink-0 overflow-hidden rounded">
              <Image
                source={selectedOption.imageSource}
                resizeMode="cover"
                style={{ width: '100%', height: '100%' }}
              />
            </View>
          ) : null}
          <Text
            className={`flex-1 text-base font-normal ${selectedOption ? 'text-primary' : 'text-muted'}`}
            numberOfLines={1}
          >
            {isEmpty ? (emptyText ?? t('common.noResults')) : (selectedOption?.label ?? placeholder ?? '')}
          </Text>
        </View>
        <ChevronDown size={20} color={colors.text.secondary} strokeWidth={1.8} />
      </Pressable>
      {portal}
      {isEmpty ? null : error ? (
        <Text className="mt-1 text-xs font-normal text-accent">{error}</Text>
      ) : helperText ? (
        <Text className="mt-1 text-xs font-normal text-secondary">{helperText}</Text>
      ) : null}
    </View>
  )
}
